import * as React from "react";
import { Box, Button, Container, Flashbar, FormField, Header, Modal, SpaceBetween } from "@amzn/awsui-components-react";
import { ErrorFlashbar } from "./ErrorFlashbar";

export interface PermissionsBaselineSectionProps {
    permissionsBaselinedDate?: Date;
    runner: {
        status: string;
        data: any;
        submitRun: (input: any) => void;
    };
    onBaseline: () => void;
    onBaselineSuccess: (output: any) => void;
}

export const PermissionsBaselineSection = (props: PermissionsBaselineSectionProps) => {
    const [flash, setFlash] = React.useState<"success" | "error">();
    const [showConfirm, setShowConfirm] = React.useState(false);

    const hasInitialized = React.useRef(false);
    const onBaselineSuccessRef = React.useRef(props.onBaselineSuccess);
    onBaselineSuccessRef.current = props.onBaselineSuccess;

    React.useEffect(() => {
        if (!hasInitialized.current) {
            hasInitialized.current = true;
            if (props.runner.status !== "Succeeded" && props.runner.status !== "Error") {
                return;
            }
        }
        if (props.runner.status === "Succeeded") {
            onBaselineSuccessRef.current(props.runner.data.output);
            setFlash("success");
        } else if (props.runner.status === "Error") {
            setFlash("error");
        } else if (props.runner.status === "Running") {
            setFlash(undefined);
        }
    }, [props.runner.status]);

    return (
        <React.Fragment>
            <Modal
                visible={showConfirm}
                onDismiss={() => setShowConfirm(false)}
                closeAriaLabel="Close confirmation dialog"
                header="Confirm Baseline Permissions"
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={() => setShowConfirm(false)}>Cancel</Button>
                            <Button variant="primary" onClick={() => {
                                setShowConfirm(false);
                                props.onBaseline();
                            }}>Confirm</Button>
                        </SpaceBetween>
                    </Box>
                }>
                Please ensure all permissions have been reviewed. Click "Confirm" to acknowledge that the current permissions reflect the intended access levels.
            </Modal>
            {flash === "success" && <Flashbar items={[{
                type: "success" as const,
                content: "Permissions successfully baselined",
                dismissible: true,
                onDismiss: () => setFlash(undefined)
            }]} />}
            {flash === "error" && <ErrorFlashbar error={props.runner.data.err} />}
            <Container
                header={<Header variant="h3"
                    actions={<Button
                        onClick={() => setShowConfirm(true)}
                        loading={props.runner.status === "Running"}
                        variant="normal">
                        Baseline Permissions
                    </Button>}>
                    Permissions Baseline
                </Header>}>
                <FormField label="Last Permissions Baselined">
                    <Box fontSize="body-m">
                        {props.permissionsBaselinedDate
                            ? new Date(props.permissionsBaselinedDate).toLocaleDateString()
                            : "Never"}
                    </Box>
                </FormField>
            </Container>
        </React.Fragment>
    );
};
